import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchCatchBoardDealContext } from 'src/utils/catchBoardApi'
import { mapCatchBoardDealContext } from 'src/utils/catchBoardMappers'

const EMPTY_CONTEXT = Object.freeze({
  dealId: null,
  primaryVenueId: null,
  linkedVenueIds: [],
  duplicateVenueWarnings: [],
  linkedOpportunities: [],
  venues: [],
})

/**
 * Lazy-load Catch Board deal context (Spartacus + Crow's Nest) for the modal.
 * Prefers a single venue via venueId for fast tab switching.
 *
 * @param {{ dealId?: string|null, venueId?: string|null, enabled?: boolean }} options
 */
export function useCatchBoardDealContext({
  dealId = null,
  venueId = null,
  enabled = true,
} = {}) {
  const [context, setContext] = useState(EMPTY_CONTEXT)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const cacheRef = useRef({})

  const refresh = useCallback(async () => {
    if (!enabled || !dealId) {
      setContext(EMPTY_CONTEXT)
      setLoading(false)
      setError(null)
      return null
    }

    const cacheKey = `${dealId}:${venueId || 'all'}`
    delete cacheRef.current[cacheKey]

    setLoading(true)
    setError(null)
    try {
      const query = {}
      if (venueId) {
        query.venueId = venueId
      }
      const raw = await fetchCatchBoardDealContext(dealId, query)
      const mapped = mapCatchBoardDealContext(raw || {})
      cacheRef.current[cacheKey] = mapped
      setContext(mapped)
      return mapped
    } catch (err) {
      setError(err.message || 'Failed to load deal context')
      setContext(EMPTY_CONTEXT)
      return null
    } finally {
      setLoading(false)
    }
  }, [dealId, venueId, enabled])

  useEffect(() => {
    if (!enabled || !dealId) {
      setContext(EMPTY_CONTEXT)
      setLoading(false)
      setError(null)
      return undefined
    }

    let cancelled = false

    async function load() {
      const cacheKey = `${dealId}:${venueId || 'all'}`
      if (cacheRef.current[cacheKey]) {
        if (!cancelled) {
          setContext(cacheRef.current[cacheKey])
          setError(null)
          setLoading(false)
        }
        return
      }

      setLoading(true)
      setError(null)
      try {
        const query = {}
        if (venueId) {
          query.venueId = venueId
        }
        const raw = await fetchCatchBoardDealContext(dealId, query)
        if (cancelled) {
          return
        }
        const mapped = mapCatchBoardDealContext(raw || {})
        cacheRef.current[cacheKey] = mapped
        setContext(mapped)
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load deal context')
          setContext(EMPTY_CONTEXT)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [dealId, venueId, enabled])

  useEffect(() => {
    // Drop cache when the deal changes so edits/relinks refetch.
    cacheRef.current = {}
  }, [dealId])

  return {
    context,
    loading,
    error,
    refresh,
  }
}

export { EMPTY_CONTEXT as EMPTY_CATCH_BOARD_DEAL_CONTEXT }
